import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
  useTheme,
} from '@mui/material';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomMarkdown from '../../../../shared/ui/CustomMarkdown';
import { BaseUrl } from '../../../../api/http';

type Chapter = {
  id: string;
  title: string;
  description: string;
  lastUpdate: string;
  readerId?: string;
};

type StoryData = {
  id: string;
  name: string;
  description: string;
  lastUpdate: string;
  cover: string;
  chapters: Chapter[];
};

const StoryAlbumCard = ({ data, freeMode = false }: { data: StoryData, freeMode: boolean }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  // ---- NOVO: controles de overflow/indícios de rolagem
  const listRef = useRef<HTMLDivElement | null>(null);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(false);

  const recompute = () => {
    const el = listRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const canScroll = scrollHeight > clientHeight + 1;
    setShowTopFade(canScroll && scrollTop > 2);
    setShowBottomFade(canScroll && scrollTop + clientHeight < scrollHeight - 2);
    setShowSwipeHint(canScroll && scrollTop + clientHeight < scrollHeight - 2);
  };

  useEffect(() => {
    recompute();
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => recompute();
    el.addEventListener('scroll', onScroll, { passive: true });
    // recalc em resize/orientação
    const onResize = () => recompute();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChapterClick = (chapterId: string) => {
    const url = freeMode ? `/g/${chapterId}?free=1` : `/g/${chapterId}`;
    navigate(url, {
      state: {
        story: data,
        fromPath: `${location.pathname}${location.search}${location.hash}`,
        freeMode,
      },
    });
  };

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        backgroundColor: theme.palette.custom.storyCardBg,
        color: theme.palette.text.primary,
        borderRadius: 0,
        border: `1px solid ${theme.palette.custom.storyCardBorder}`,
        overflow: 'hidden',
        maxWidth: '100%',
      }}
    >
      <Box
        component="img"
        src={`${BaseUrl}${data.cover ?? ''}`}
        alt={data.name}
        sx={{
          width: { xs: '100%', md: 200 },
          height: 'auto',
          objectFit: 'cover',
          borderRight: { md: `1px solid ${theme.palette.custom.storyCardBorder}` },
        }}
      />

      <Box sx={{ p: 2, flex: 1, position: 'relative' }}>
        <Typography variant="h6" sx={{ color: theme.palette.custom.storyCardHeaderText, mb: 0.5 }}>
          {data.name}
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
          <Button
            variant="outlined"
            endIcon={<ArrowForwardOutlinedIcon />}
            onClick={() => data.chapters[0] && handleChapterClick(data.chapters[0].id)}
            disabled={data.chapters.length === 0}
            sx={{ alignSelf: 'flex-start' }}
          >
            Open story
          </Button>
        </Stack>

        <Box
          sx={{
            mb: 1,
            backgroundColor: theme.palette.custom.scrollbarThumb,
            p: 1.5,
            borderRadius: 1,
          }}
        >
          <CustomMarkdown>{data.description}</CustomMarkdown>
        </Box>

        <Divider sx={{ borderColor: '#666', my: 1 }} />

        <Typography variant="subtitle2" sx={{ color: theme.palette.custom.storyCardHeaderText, mb: 1 }}>
          Chapters:
        </Typography>

        {/* ---- NOVO: wrapper com fades e dica */}
        <Box sx={{ position: 'relative' }}>
          <Box
            ref={listRef}
            sx={{
              maxHeight: 140, // um pouco maior pra mobile perceber a rolagem
              overflowY: 'auto',
              pr: 1,
              // Firefox
              scrollbarWidth: 'auto',
              scrollbarColor: `${theme.palette.primary.main} ${theme.palette.custom.scrollbarTrack}`,
              // iOS suaviza a rolagem
              WebkitOverflowScrolling: 'touch',
              // WebKit/Chromium
              '&::-webkit-scrollbar': {
                width: '12px', // mais visível
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.custom.scrollbarTrack,
                borderRadius: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.primary.main, // bem contrastante
                borderRadius: '8px',
                border: `3px solid ${theme.palette.custom.scrollbarTrack}`, // “trilho” aparente
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: theme.palette.primary.light,
              },
            }}
          >
            <List dense disablePadding>
              {data.chapters.map((chapter) => (
                <ListItem
                  key={chapter.id}
                  sx={{
                    px: 1,
                    py: 0.5,
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    '&:hover': {
                      backgroundColor: theme.palette.custom.hoverBg,
                    },
                  }}
                  onClick={() => handleChapterClick(chapter.id)}
                >
                  <ListItemText
                    primary={
                      <Typography sx={{ color: theme.palette.custom.chapterTitle, fontSize: 13, fontWeight: 500 }}>
                        {chapter.title}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" sx={{ color: theme.palette.custom.chapterSubtitle, fontSize: 11 }}>
                        Last update:{' '}
                        {new Date(chapter.lastUpdate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Gradiente superior/inferior como pista de rolagem */}
          {showTopFade && (
            <Box
              sx={{
                pointerEvents: 'none',
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                height: 18,
                background: `linear-gradient(to bottom, ${theme.palette.background.paper}, rgba(0,0,0,0))`,
              }}
            />
          )}
          {showBottomFade && (
            <Box
              sx={{
                pointerEvents: 'none',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: 18,
                background: `linear-gradient(to top, ${theme.palette.background.paper}, rgba(0,0,0,0))`,
              }}
            />
          )}

          {/* Dica “Swipe to scroll” só em telas pequenas e apenas quando há mais conteúdo */}
          {showSwipeHint && (
            <Box
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                position: 'absolute',
                right: 8,
                bottom: 6,
                px: 1,
                py: 0.25,
                fontSize: 10,
                borderRadius: 1,
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${theme.palette.divider}`,
                color: theme.palette.text.secondary,
                opacity: 0.9,
                pointerEvents: 'none',
              }}
            >
              Swipe to scroll
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default StoryAlbumCard;
